import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { VehicleFormData } from '../types';
import { AdminPanel } from '../components/AdminPanel';
import { Header } from '../components/Header';
import { useToast } from '@/hooks/use-toast';
import { useVehicles, useCreateVehicle, useUpdateVehicle, useDeleteVehicle } from '../hooks/useVehicles';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';

const Admin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdminLoggedIn, isInitialized, logoutAdmin } = useAdminAuth();
  
  const { data: vehicles = [], isLoading, error } = useVehicles();
  const createVehicleMutation = useCreateVehicle();
  const updateVehicleMutation = useUpdateVehicle();
  const deleteVehicleMutation = useDeleteVehicle();

  // Redirect to login if not authenticated (only after initialization)
  useEffect(() => {
    console.log('🔄 Admin auth check - isAdminLoggedIn:', isAdminLoggedIn, 'isInitialized:', isInitialized);
    if (isInitialized && !isAdminLoggedIn) {
      navigate('/');
    }
  }, [isAdminLoggedIn, isInitialized, navigate]);

  const handleAddVehicle = async (data: VehicleFormData) => {
    try {
      await createVehicleMutation.mutateAsync(data);
      toast({
        title: "Annonce ajoutée",
        description: "La nouvelle annonce a été publiée avec succès.",
      });
    } catch (error) {
      console.error('Error adding vehicle:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter l'annonce.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateVehicle = async (id: string, data: VehicleFormData) => {
    try {
      await updateVehicleMutation.mutateAsync({ id, data });
      toast({
        title: "Annonce modifiée",
        description: "L'annonce a été mise à jour avec succès.",
      });
    } catch (error) {
      console.error('Error updating vehicle:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'annonce.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteVehicle = async (id: string) => {
    try {
      await deleteVehicleMutation.mutateAsync(id);
      toast({
        title: "Annonce supprimée",
        description: "L'annonce a été supprimée avec succès.",
        variant: "destructive",
      });
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'annonce.",
        variant: "destructive",
      });
    }
  };

  const handleAdminClick = () => {
    // Logout and redirect to home
    logoutAdmin();
    navigate('/');
  };

  // Wait for initialization before deciding what to render
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdminLoggedIn) {
    return null; // Component will redirect
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-600 mb-2">Erreur de chargement</h2>
            <p className="text-gray-600">Impossible de charger les données. Veuillez réessayer plus tard.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header 
        onAdminClick={handleAdminClick}
        isAdmin={true}
      />
      
      <main>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AdminPanel
            vehicles={vehicles}
            onAddVehicle={handleAddVehicle}
            onUpdateVehicle={handleUpdateVehicle}
            onDeleteVehicle={handleDeleteVehicle}
            isLoading={isLoading}
          />
        </div>
      </main>
    </div>
  );
};

export default Admin;